import React from "react";

interface ModeRendererProps {
  brand: string;
  today: string;
  Copy: string;
}

export function VasarelyAirbusMode({ brand }: ModeRendererProps) {
  const width = 794;
  const height = 1123;

  // Airbus aerospace blue gradient background
  const bgGradient = (
    <defs>
      <linearGradient id="airbusGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{ stopColor: "#003D7A", stopOpacity: 1 }} />
        <stop offset="50%" style={{ stopColor: "#0066B3", stopOpacity: 1 }} />
        <stop offset="100%" style={{ stopColor: "#00A3E0", stopOpacity: 1 }} />
      </linearGradient>
    </defs>
  );

  // Wing lattices - aerodynamic wing shape tessellation
  const wingLattices: JSX.Element[] = [];
  const wingsX = 12;
  const wingsY = 16;
  const cellW = 60;
  const cellH = 65;
  for (let i = 0; i < wingsX; i++) {
    for (let j = 0; j < wingsY; j++) {
      const x = -360 + i * cellW;
      const y = -520 + j * cellH;
      // Aerodynamic wing shape with swept angle
      const sweep = Math.sin(i * 0.3) * 15;
      const path = `M ${x},${y + sweep} L ${x + cellW * 0.8},${y + cellH * 0.3 + sweep} L ${x + cellW * 0.8},${y + cellH * 0.7 + sweep} L ${x},${y + cellH + sweep} Z`;
      wingLattices.push(
        <path
          key={`wing-${i}-${j}`}
          d={path}
          fill="none"
          stroke="#FFFFFF"
          strokeWidth={1.5}
          opacity={0.15}
        />
      );
    }
  }

  // Altitude grids - horizontal layers representing flight levels
  const altitudeGrids: JSX.Element[] = [];
  for (let layer = 0; layer < 18; layer++) {
    const y = -500 + layer * 60;
    const opacity = 0.4 - layer * 0.015;
    altitudeGrids.push(
      <line
        key={`altitude-${layer}`}
        x1={-360}
        y1={y}
        x2={360}
        y2={y}
        stroke="#FFFFFF"
        strokeWidth={2}
        opacity={opacity}
        strokeDasharray={`${10 + layer * 2},${5 + layer}`}
      />
    );
  }

  // Aerodynamic arcs - smooth flight path curves
  const aerodynamicArcs: JSX.Element[] = [];
  for (let arc = 0; arc < 8; arc++) {
    const startX = -340 + arc * 85;
    const amplitude = 100 + arc * 20;
    const points: string[] = [];
    for (let p = 0; p <= 40; p++) {
      const t = p / 40;
      const x = startX + t * 680;
      const y = -480 + Math.sin(t * Math.PI * 2 + arc * 0.4) * amplitude;
      points.push(`${x},${y}`);
    }
    aerodynamicArcs.push(
      <polyline
        key={`arc-${arc}`}
        points={points.join(" ")}
        fill="none"
        stroke="#00A3E0"
        strokeWidth={1.5}
        opacity={0.3}
      />
    );
  }

  // Precision freedom nodes - circular waypoints with crosshairs
  const precisionNodes: JSX.Element[] = [];
  const nodePositions = [
    [-280, -420],
    [0, -480],
    [280, -420],
    [-200, -120],
    [200, -120],
    [0, 180],
    [-250, 420],
    [250, 420],
  ];
  nodePositions.forEach(([nx, ny], i) => {
    precisionNodes.push(
      <g key={`node-${i}`}>
        <circle
          cx={nx}
          cy={ny}
          r={35}
          fill="none"
          stroke="#FFFFFF"
          strokeWidth={2.5}
          opacity={0.6}
        />
        <circle
          cx={nx}
          cy={ny}
          r={25}
          fill="none"
          stroke="#00A3E0"
          strokeWidth={1.5}
          opacity={0.5}
        />
        <line
          x1={nx - 40}
          y1={ny}
          x2={nx + 40}
          y2={ny}
          stroke="#FFFFFF"
          strokeWidth={1.5}
          opacity={0.5}
        />
        <line
          x1={nx}
          y1={ny - 40}
          x2={nx}
          y2={ny + 40}
          stroke="#FFFFFF"
          strokeWidth={1.5}
          opacity={0.5}
        />
      </g>
    );
  });

  // Flight path network - connecting nodes with trajectory lines
  const flightPaths: JSX.Element[] = [];
  const connections = [
    [0, 1],
    [1, 2],
    [0, 3],
    [2, 4],
    [3, 5],
    [4, 5],
    [5, 6],
    [5, 7],
  ];
  connections.forEach(([a, b], i) => {
    const [x1, y1] = nodePositions[a];
    const [x2, y2] = nodePositions[b];
    flightPaths.push(
      <line
        key={`path-${i}`}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="#FFFFFF"
        strokeWidth={2}
        opacity={0.25}
        strokeDasharray="10,5"
      />
    );
  });

  // Central Airbus logo abstraction - stylized "A" with wings
  const airbusLogo = (
    <g opacity={0.15}>
      <path
        d="M 0,-50 L -60,50 L -30,50 L 0,0 L 30,50 L 60,50 Z"
        fill="none"
        stroke="#FFFFFF"
        strokeWidth={4}
      />
      <ellipse cx={-45} cy={30} rx={35} ry={15} fill="none" stroke="#FFFFFF" strokeWidth={2.5} />
      <ellipse cx={45} cy={30} rx={35} ry={15} fill="none" stroke="#FFFFFF" strokeWidth={2.5} />
    </g>
  );

  return (
    <svg width={width} height={height} xmlns="http://www.w3.org/2000/svg">
      {bgGradient}
      <rect width={width} height={height} fill="url(#airbusGradient)" />
      <g transform={`translate(${width / 2}, ${height / 2})`}>
        {wingLattices}
        {altitudeGrids}
        {aerodynamicArcs}
        {flightPaths}
        {precisionNodes}
        {airbusLogo}
      </g>
    </svg>
  );
};
